"use client";

import { useTranslations } from "next-intl";
import { useState } from "react";
import { toast } from "sonner";
import type { ReviewForClient } from "@/features/reviews/shared/types";
import { Button, HStack, Text, VStack } from "@/ui";
import { useCanReview } from "../hooks/useCanReview";
import { useReviews } from "../hooks/useReviews";
import { ReviewCard } from "./ReviewCard";
import { ReviewForm } from "./ReviewForm";

interface ReviewsListProps {
  productId: string;
  initialReviews?: ReviewForClient[];
}

export function ReviewsList({ productId }: ReviewsListProps) {
  const reviewsT = useTranslations("reviews");
  const generalT = useTranslations("general");
  const notFoundT = useTranslations("not_found");
  const toastT = useTranslations("toast");

  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);

  const { data, isLoading, error } = useReviews({
    productId,
    limit: 10,
    page,
  });

  const { data: canReviewData, isFetching: isCheckingCanReview, refetch } = useCanReview(productId);

  const reviews = data?.docs || [];
  const totalPages = data?.totalPages || 1;
  const cannotReviewReason =
    canReviewData && "reason" in canReviewData ? canReviewData.reason : undefined;

  const handleWriteReviewClick = async () => {
    const { data: eligibility, error: eligibilityError } = await refetch();
    if (eligibilityError) {
      toast.error(toastT("error.title"), {
        description: toastT("error.message"),
      });
      return;
    }

    const reason = eligibility && "reason" in eligibility ? eligibility.reason : undefined;

    if (reason === "NOT_PURCHASED") {
      toast.error(toastT("error.title"), {
        description: reviewsT("purchase_required"),
      });
      return;
    }
    if (reason === "NOT_LOGGED_IN") {
      toast.error(toastT("error.title"), {
        description: reviewsT("login_required"),
      });
      return;
    }
    if (reason === "ALREADY_REVIEWED") {
      toast.error(toastT("error.title"), {
        description: reviewsT("already_reviewed"),
      });
      return;
    }
    if (eligibility?.canReview) setShowForm(true);
  };

  if (isLoading) {
    return <Text className="text-center text-muted-foreground py-8">{generalT("loading")}...</Text>;
  }

  if (error) {
    return <Text className="text-center text-muted-foreground py-8">{generalT("loading")}...</Text>;
  }

  const showWriteReviewSection = cannotReviewReason !== "ALREADY_REVIEWED";

  return (
    <VStack className="gap-6">
      {/* Write Review Section */}
      {showWriteReviewSection && (
        <VStack className="gap-4">
          {!showForm ? (
            <VStack className="gap-2">
              <Text className="font-semibold">{reviewsT("write_review")}</Text>
              <Text className="text-sm text-muted-foreground">{reviewsT("review_incentive")}</Text>
              <Button
                onClick={handleWriteReviewClick}
                className="self-start"
                disabled={isCheckingCanReview}
              >
                {reviewsT("write_review")}
              </Button>
            </VStack>
          ) : (
            <VStack className="gap-4 border rounded-lg p-6 bg-muted/30">
              <HStack className="justify-between items-center">
                <Text className="font-semibold">{reviewsT("write_review")}</Text>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowForm(false)}
                >
                  {generalT("close")}
                </Button>
              </HStack>
              <ReviewForm
                productId={productId}
                onSuccess={() => setShowForm(false)}
              />
            </VStack>
          )}
        </VStack>
      )}

      {/* Reviews List */}
      {reviews.length === 0 ? (
        <Text className="text-center text-muted-foreground py-8">{notFoundT("no_reviews")}</Text>
      ) : (
        <VStack className="gap-0">
          {reviews.map((review) => (
            <ReviewCard
              key={review.id}
              review={review}
            />
          ))}
        </VStack>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <HStack className="justify-center gap-2 mt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            {generalT("back")}
          </Button>
          <Text className="text-sm px-4">
            {page} / {totalPages}
          </Text>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            {generalT("view_all")}
          </Button>
        </HStack>
      )}
    </VStack>
  );
}
